<header class="head">
  <div class="head__title">
    <h2 class="card-title">{{ title() }}</h2>
    @if (holdersStatus() !== 'not-disclosed' && holders().length > 0) {
      <span class="head__summary">{{ summary() }}</span>
    }
  </div>
  @if (source(); as s) {
    <app-charter-provenance [from]="s" variant="source" [current]="true" />
  }
</header>

@if (holdersStatus() === 'not-disclosed' || holders().length === 0) {
  <div class="undisclosed">
    <i class="pi pi-info-circle" aria-hidden="true"></i>
    <div>
      <p class="undisclosed__title">
        {{
          isCorporation()
            ? 'Acionistas não informados no documento'
            : 'Sócios não informados no documento'
        }}
      </p>
      <p class="undisclosed__text">
        @if (isCorporation()) {
          O estatuto e as atas lidos não trazem a lista de acionistas. O Cog não completa essa lista
          por conta própria. Para ter a composição, envie o livro de registro de ações ou uma
          declaração da companhia.
        } @else {
          Os atos lidos não trazem o quadro de sócios. O Cog não completa essa lista por conta
          própria. Envie a consolidação do contrato social ou a última alteração com o quadro.
        }
      </p>
    </div>
  </div>
} @else {
  @if (holdersStatus() === 'partial') {
    <p class="partial-note">
      <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
      {{
        isCorporation()
          ? 'Acionistas citados em atas (lista parcial).'
          : 'Sócios citados em atas (lista parcial).'
      }}
      A soma das participações não é cobrada.
    </p>
  }

  <div class="table-wrap">
    <table class="holders">
      <thead>
        <tr>
          <th>{{ holderNoun() }}</th>
          <th>Documento</th>
          <th class="num">{{ unitsHeader() }}</th>
          <th class="share-col">Participação</th>
          <th>Assina pela empresa</th>
          <th>Receita</th>
          <th><span class="sr-only">Origem e histórico</span></th>
        </tr>
      </thead>
      <tbody>
        @for (row of rows(); track row.holder.personId) {
          @let holder = row.holder;
          @let open = expanded().has(holder.personId);
          <tr
            class="prov-row"
            [class.is-warning]="holder.receitaStatus === 'missing-in-receita'"
            [class.is-open]="open">
            <td class="name">
              <span class="name__line">
                <span class="name__text">{{ holder.name }}</span>
                @if (row.entityTag; as tag) {
                  <span class="tag tag--entity" [title]="tag.hint">
                    <i class="pi" [class]="'pi ' + tag.icon" aria-hidden="true"></i>
                    {{ tag.label }}
                  </span>
                }
              </span>
              @if (holder.representative?.name; as repName) {
                <small class="name__rep">
                  representada por {{ repName }}
                  @if (holder.representative?.documentMasked; as repDoc) {
                    <span class="nowrap">· {{ repDoc }}</span>
                  }
                </small>
              }
              @if (row.changed && row.source.actLabel) {
                <small class="name__since">
                  vigente desde {{ row.source.actLabel }}
                  @if (row.source.actDate) {
                    ({{ row.source.actDate | vaultDate }})
                  }
                </small>
              }
            </td>
            <td>
              <app-charter-person-document
                [personId]="holder.personId"
                [masked]="holder.documentMasked"
                [canReveal]="holder.canRevealDocument"
                [personKind]="holder.personKind" />
            </td>
            <td class="num">
              {{ formatUnits(holder.units.value) }}
              @if (unitsByClass(holder); as byClass) {
                <small class="sub">{{ byClass }}</small>
              }
            </td>
            <td class="share-col">
              <div class="share">
                <span class="share__pct">
                  {{ formatPercent(holder.percentage.value) }}
                  @if (holder.percentage.value !== null && !holder.percentageStated) {
                    <small
                      class="share__calc"
                      title="O documento não escreve o percentual: veio das quotas ou ações"
                      >calculado</small
                    >
                  }
                </span>
                <span class="share__bar" aria-hidden="true">
                  <span class="share__fill" [style.width.%]="holder.percentage.value ?? 0"></span>
                </span>
              </div>
              <small class="sub">{{ money(holder.capitalAmount.value) }}</small>
            </td>
            <td>
              <span
                class="sign"
                [class]="'sign sign--' + row.signing.tone"
                [pTooltip]="row.signing.tooltip ?? ''"
                [tooltipDisabled]="!row.signing.tooltip"
                tooltipPosition="top"
                [tabindex]="row.signing.tooltip ? 0 : -1">
                <i class="pi" [class]="'pi ' + signIcon[row.signing.tone]" aria-hidden="true"></i>
                {{ row.signing.label }}
              </span>
            </td>
            <td>
              <span class="receita" [class]="'receita receita--' + holder.receitaStatus">
                @if (holder.receitaStatus === 'match') {
                  <i class="pi pi-check" aria-hidden="true"></i>
                } @else if (holder.receitaStatus === 'missing-in-receita') {
                  <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                }
                {{ receitaLabel[holder.receitaStatus] }}
              </span>
            </td>
            <td class="actions">
              <app-charter-provenance
                [from]="row.source"
                [current]="true"
                variant="icon"
                [base]="source()" />
              @if (row.history.length > 0) {
                <button
                  type="button"
                  class="link-btn"
                  [attr.aria-expanded]="open"
                  (click)="toggleHistory(holder.personId)">
                  <i
                    class="pi"
                    [class.pi-history]="!open"
                    [class.pi-chevron-up]="open"
                    aria-hidden="true"></i>
                  {{ open ? 'Fechar' : 'Histórico (' + row.history.length + ')' }}
                </button>
              }
            </td>
          </tr>
          @if (open) {
            <tr class="history-row">
              <td colspan="7">
                <app-charter-history-list
                  [rows]="row.history"
                  [currentActId]="row.source.actId"
                  [label]="'Histórico de ' + holder.name" />
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>
}

@if (receitaOnly().length > 0) {
  <div class="receita-only" role="note">
    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
    <span>
      <b>Na Receita Federal, mas não no contrato vigente:</b>
      @for (person of receitaOnly(); track person.name; let last = $last) {
        {{ person.name }}
        @if (person.qualification) {
          <span class="receita-only__muted">({{ person.qualification }})</span>
        }
        @if (!last) {
          ;
        }
      }
      <span class="receita-only__muted">
        · divergência só gera aviso, nunca altera o cadastro.</span
      >
    </span>
  </div>
}
